import Badge from "@/components/atoms/Badge";
import Container from "@/components/atoms/Container";
import SowForm, {
  SowProps,
  useSowForm,
} from "@/components/organisms/Sow/Form";
import Detail from "@/components/organisms/Sow/Detail";
import useAccess from "@/hooks/useAccess";
import Sow from "@/types/Sow";
import { Button, Text } from "@radix-ui/themes";
import { PenSquare, X, CheckCheck, Copy } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { router } from "@inertiajs/react";
import useMessageProps from "@/hooks/useMessageProps";
import { Tabs, Box } from "@radix-ui/themes";
import ResourceTab from "@/components/organisms/Sow/Tabs/ResourceTab";
import StandardBreadcrumb from "@/components/molecules/StandardBreadcrumb";
import axios from 'axios';
import ServiceType from "@/types/ServiceType";
import InternalTicketType from "@/types/InternalTicketType";
import { Switch } from "@/components/atoms/Switch";
import OutputTab from "@/components/organisms/Sow/Tabs/OutputTab";
import AdditionalCostTab from "@/components/organisms/Sow/Tabs/AdditionalCostTab";
import DiscountTab from "@/components/organisms/Sow/Tabs/DiscountTab";
import HistoryTab from "@/components/organisms/Sow/Tabs/HistoryTab";
import History from "@/types/History";

export type SowPageProps = SowProps & {
  sow: Sow;
  redirect_props: any;
  additional_cost_types: string[];
  additional_cost_payment_types: string[];
  additional_cost_amf_types: string[];
  discount_payment_types: string[];
  discount_types: string[];
  histories: History[];
};

const ShowSow = (
  {
    sow,
    redirect_props,
    additional_cost_types,
    additional_cost_payment_types,
    additional_cost_amf_types,
    discount_types,
    discount_payment_types,
    histories,
    ...rest
  }: SowPageProps) => {
  const form = useSowForm(sow);
  const [loading, setLoading] = useState(false);
  const [isInfoTab, setIsInfoTab] = useState(true);
  const { getAccess } = useAccess();
  const [edit, setEdit] = useState(false);
  const tabRef = useRef(null);
  const [active, setActive] = useState("info");
  const [restPropsData, setRestPropsData] = useState(rest);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [internalTicketTypes, setInternalTicketTypes] = useState<InternalTicketType[]>([]);
  const [unfreezeSow, setUnfreezeSow] = useState<boolean>(false);

  const breadcrumbItems = [
    {
      label: "Sows",
      onClick: () => router.get("/sows"),
    },
    {
      label: sow.display_id,
    },
  ];

  useMessageProps({ redirect_props });

  const onSubmit = (data: any) => {
    router.put(
      `/sows/${sow.id}`,
      data,
      {
        onStart: () => setLoading(true),
        onFinish: () => setLoading(false),
      }
    );
  };

  useEffect(() => {
    if (!loading && redirect_props?.success) {
      setEdit(false);
    }
  }, [loading]);

  useEffect(() => {
    if (tabRef?.current)
      [...(tabRef?.current as any).children]
        .find((el: any) => el.dataset.state === "active")
        .scrollIntoView({
          inline: "center",
        });
  }, [active]);

  useEffect(() => {
    const fetchData = async () => {
      const response = await axios.get(`/accounts/${sow.account.id}/read_account`);



      setServiceTypes(response.data.service_types);
      setInternalTicketTypes(response.data.internal_ticket_types);

      setRestPropsData({
        ...rest,
        entities: response.data.entities,
        nmq_entities: response.data.entities.find((entity: any) => entity.id === sow.entity?.id)?.nmq_entities || [],
        functions: response.data.functions,
        departments: response.data.departments,
        countries: response.data.countries,
        sows: response.data.sows,
      });
    };
    
    fetchData();
  }, []);

    const CheckSowDate = ({ date }: { date: string }) => {
    const checkIfPastDate = (date: string) => {
      const inputDate = new Date(date);
      const currentDate = new Date();

      return inputDate < currentDate;
    };

    const isExpired = checkIfPastDate(date);
    
    return (
      <Badge
        variant={
          !isExpired
            ? "success"
            : "destructive"
        }
      >
        {isExpired ? "Expired" : "Active"}
      </Badge>
    );
  };

  const handleApproveSow = () => () => {
    router.get(
      `/sows/${sow.id}/approve`,
      {},
      {
        onStart: () => setLoading(true),
        onFinish: () => setLoading(false),
      }
    );
  };

  const handleCopySow = () => () => {
    const isConfirmed = window.confirm("Are you sure you want to copy this SOW?");

    if (isConfirmed) {
      router.get(
        `/sows/${sow.id}/copy`,
        {},
        {
          onStart: () => setLoading(true),
          onFinish: () => setLoading(false),
        }
      );
    }
  };

  const handleUnfreezeSow = (isChecked: boolean) => {
    if (isChecked) {
      setUnfreezeSow(true);
    } else {
      setUnfreezeSow(false);
    }
  };

  return (
    <>
      <Container>
        <StandardBreadcrumb items={breadcrumbItems} />
        <div className="flex items-center justify-between mb-6 pt-2">
          <div className="flex items-center gap-2">
            <Text size="6">{sow.display_id}  {sow.currency}</Text>
            <CheckSowDate date={sow.end_date} />
          </div>
          {isInfoTab && (
            <div className="flex">
              {edit && getAccess(["unfreeze_sow"]) && (sow?.invoiced_periods || []).length > 0  && (
                <div className="mr-2">
                  <label className="mr-2 font-semibold text-gray-700">Unfreeze Fields:</label>
                  <Switch
                    checked={unfreezeSow}
                    onCheckedChange={handleUnfreezeSow}
                  />
                </div>
              )}
              {!sow.admin_approval && (
                <div className="mr-2">
                  <Button
                    onClick={handleApproveSow()}
                    disabled={!getAccess(["approve_sow"])}
                  >
                    <>
                      <CheckCheck size={16} />
                      <span>Approve</span>
                    </>
                  </Button>
                </div>
              )}
              <div className="mr-2">
                <Button
                  onClick={handleCopySow()}
                  disabled={!getAccess(["copy_sow"])}
                >
                  <>
                    <Copy size={16} />
                    <span>Copy</span>
                  </>
                </Button>
              </div>
              <Button
                onClick={() => setEdit(!edit)}
                disabled={!getAccess(["update_sow"])}
              >
                {edit ? (
                  <>
                    <X size={16} className="mr-2" /> <span>Cancel</span>
                  </>
                ) : (
                  <>
                    <PenSquare size={16} />
                    <span>Edit</span>
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </Container>
      <Tabs.Root
        value={active}
        onValueChange={(tab) => {
          const isInfoTab = tab === "info";
          setIsInfoTab(isInfoTab);
          if (edit && isInfoTab) setEdit(false);
          setActive(tab);
        }}
        className="account-tabs"
      >
        <Tabs.List className={"px-2"} ref={tabRef}>
          <Tabs.Trigger value="info">Information</Tabs.Trigger>
          
          {sow.payment_type != 'Output Based' && (
            <Tabs.Trigger value="resources">Resources</Tabs.Trigger>
          )}

          {sow.payment_type == 'Output Based' && (
            <Tabs.Trigger value="outputs">Outputs</Tabs.Trigger>
          )}
          
          <Tabs.Trigger value="additionalCosts">Additional Costs</Tabs.Trigger>
          <Tabs.Trigger value="discounts">Discounts</Tabs.Trigger>
          <Tabs.Trigger value="updateHistroy">Update History</Tabs.Trigger>
        </Tabs.List>

        <Box>
          <Tabs.Content value="info">
            <Container className="py-4">
            {edit ? (
                <SowForm
                form={form}
                edit={edit}
                sow={sow}
                onSubmit={onSubmit}
                data={restPropsData}
                loading={loading}
                errors={redirect_props?.errors}
                unfreezeSow={unfreezeSow}
              />
              ) : (
                <Detail sow={sow} />
              )}

            </Container>
          </Tabs.Content>
          
          <Tabs.Content value="resources">
            {getAccess(["listing_sow_resources"]) ? (
              <ResourceTab sow={sow} redirect_props={redirect_props} serviceTypes={serviceTypes}/>
            ) : (
              <span className="inline-flex items-center gap-2 text-gray-500 cursor-not-allowed text-center !block mt-32 text-lg">
                You are not authorized to view this content.
              </span>
            )}
          </Tabs.Content>
  
          <Tabs.Content value="outputs">
            {getAccess(["listing_sow_outputs"]) ? (
              <OutputTab sow={sow} redirect_props={redirect_props} serviceTypes={serviceTypes} internalTicketTypes={internalTicketTypes}/>
            ) : (
              <span className="inline-flex items-center gap-2 text-gray-500 cursor-not-allowed text-center !block mt-32 text-lg">
                You are not authorized to view this content.
              </span>
            )}
          </Tabs.Content>
 
          <Tabs.Content value="additionalCosts">
            {getAccess(["listing_sow_additional_costs"]) ? (
              <AdditionalCostTab sow={sow} redirect_props={redirect_props} costTypes={additional_cost_types} paymentTypes={additional_cost_payment_types} amfTypes={additional_cost_amf_types}/>
            ) : (
              <span className="inline-flex items-center gap-2 text-gray-500 cursor-not-allowed text-center !block mt-32 text-lg">
                You are not authorized to view this content.
              </span>
            )}
          </Tabs.Content>

          <Tabs.Content value="discounts">
            {getAccess(["listing_sow_discounts"]) ? (
              <DiscountTab sow={sow} redirect_props={redirect_props} discountTypes={discount_types} paymentTypes={discount_payment_types}/>
            ) : (
              <span className="inline-flex items-center gap-2 text-gray-500 cursor-not-allowed text-center !block mt-32 text-lg">
                You are not authorized to view this content.
              </span>
            )}
          </Tabs.Content>
          
          <Tabs.Content value="updateHistroy">
            {getAccess(["listing_sow_histories"]) ? (
              <HistoryTab histories={histories} />
            ) : (
              <span className="inline-flex items-center gap-2 text-gray-500 cursor-not-allowed text-center !block mt-32 text-lg">
                You are not authorized to view this content.
              </span>
            )}
          </Tabs.Content>
        </Box>
      </Tabs.Root>
    </>
  );
};

export default ShowSow;
